import React, { useState } from 'react';
import LegendParleyWorkspace from './LegendParleyWorkspace';
import { isSuperadminRole } from '../constants';

function formatHistoryTimestamp(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function LegendParleyApp({
  authProfile,
  onLogout,
  showHistory,
  setShowHistory,
  chatHistory,
  historyLoading,
  sessionId,
  onSelectConversation,
  onDeleteConversation,
  onClearHistory,
  onNewConversation,
  startingNewChat = false,
  openAdminPortal,
  isSuperadmin,
  apiFetch,
  error,
  setError,
  canSwitchProductMode,
  entitledModeOptions,
  onConsultantModeChange,
  savedReportHtml,
  savedReportTitle,
  savedUploadedFiles,
  onUploadedFilesChange,
  savedDraft,
  onDraftChange,
  onReportHtmlUpdated,
  savedReportChatMessages = [],
  onReportChatMessagesChange,
  onConversationRefresh,
}) {
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);
  const [analysisRunning, setAnalysisRunning] = useState(false);

  const handleNewChatClick = () => {
    if (startingNewChat) return;
    if (analysisRunning) {
      setShowNewChatConfirm(true);
      return;
    }
    onNewConversation?.();
  };

  return (
    <div className="app-shell bg-[#0c0f12] text-[#e8e2d6] text-sm legend-parley-shell">
      <header className="border-b border-[#3a342c] shrink-0">
        <div className="container mx-auto px-4 py-2 flex flex-wrap items-center justify-end gap-1.5">
          {canSwitchProductMode && entitledModeOptions?.length > 1 && (
            <select
              value="legend_parley"
              onChange={(e) => onConsultantModeChange?.(e.target.value)}
              className="px-2 py-1.5 bg-[#14181d] border border-[#3a342c] rounded-lg text-xs text-[#e8e2d6]"
              aria-label="Product mode"
            >
              {entitledModeOptions.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setShowHistory?.(!showHistory)}
            className="px-2 py-1.5 border border-[#3a342c] rounded-lg text-xs hover:border-[#c4a35a]/50"
          >
            History
          </button>
          <button
            type="button"
            onClick={handleNewChatClick}
            disabled={startingNewChat}
            className="px-2 py-1.5 border border-[#3a342c] rounded-lg text-xs hover:border-[#c4a35a]/50 disabled:opacity-40"
          >
            New case
          </button>
          {isSuperadminRole(authProfile?.role) && (
            <button
              type="button"
              onClick={openAdminPortal}
              className="px-2 py-1.5 bg-[#c4a35a]/20 border border-[#c4a35a]/60 text-[#c4a35a] font-semibold rounded-lg text-xs"
              title="Create organizations and manage platform users"
            >
              Admin · Orgs
            </button>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="px-2 py-1.5 border border-[#3a342c] rounded-lg text-xs hover:border-[#c4a35a]/50"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {showHistory && (
          <aside className="w-72 border-r border-[#3a342c] bg-[#101418] overflow-auto shrink-0">
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">Cases</h2>
                <p className="text-[10px] text-[#6f675c] mt-0.5">Open a case or delete individual entries.</p>
              </div>
              {(chatHistory || []).length > 0 && (
                <button
                  type="button"
                  className="text-xs text-red-300/80 hover:text-red-300 shrink-0"
                  onClick={() => onClearHistory?.()}
                >
                  Clear all
                </button>
              )}
            </div>
            {historyLoading ? (
              <p className="px-3 text-xs text-[#6f675c]">Loading…</p>
            ) : (chatHistory || []).length === 0 ? (
              <p className="px-3 text-xs text-[#6f675c]">No saved cases yet.</p>
            ) : (
              <ul className="px-2 pb-4 space-y-1">
                {(chatHistory || []).map((item) => (
                  <li key={item.session_id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelectConversation?.(item.session_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectConversation?.(item.session_id);
                        }
                      }}
                      className={`w-full text-left px-2 py-2 rounded-md text-xs cursor-pointer flex items-start justify-between gap-2 ${
                        item.session_id === sessionId
                          ? 'bg-[#241f19] text-[#c4a35a]'
                          : 'hover:bg-[#1a1f24] text-[#cfc6b6]'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{item.title || 'Untitled case'}</div>
                        <div className="text-[10px] text-[#6f675c]">
                          {formatHistoryTimestamp(item.updated_at || item.created_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 text-[#6f675c] hover:text-red-300 p-1"
                        title="Delete case"
                        aria-label={`Delete ${item.title || 'case'}`}
                        onClick={(event) => onDeleteConversation?.(item.session_id, event)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}

        <LegendParleyWorkspace
          key={sessionId || 'legend-parley-empty'}
          sessionId={sessionId}
          apiFetch={apiFetch}
          error={error}
          onError={setError}
          savedReportHtml={savedReportHtml}
          savedReportTitle={savedReportTitle}
          savedUploadedFiles={savedUploadedFiles}
          onUploadedFilesChange={onUploadedFilesChange}
          savedDraft={savedDraft}
          onDraftChange={onDraftChange}
          onReportHtmlUpdated={(payload) => {
            setAnalysisRunning(false);
            onReportHtmlUpdated?.(payload);
          }}
          savedReportChatMessages={savedReportChatMessages}
          onReportChatMessagesChange={onReportChatMessagesChange}
          onConversationRefresh={onConversationRefresh}
        />
      </div>

      {showNewChatConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#14181d] border border-[#3a342c] rounded-lg p-4 max-w-sm w-full space-y-3">
            <p className="text-sm">Start a new case? Unsaved progress on the current run may be lost.</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-xs border border-[#3a342c] rounded-md"
                onClick={() => setShowNewChatConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs bg-[#c4a35a] text-[#14110c] rounded-md font-semibold"
                onClick={() => {
                  setShowNewChatConfirm(false);
                  onNewConversation?.();
                }}
              >
                New case
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
